/**
 * ApiKeyGateModal — the global "you're out of free messages" gate.
 *
 * Mounted once at the app root (App.tsx). Any LLM-triggering request that
 * the server blocks (middleware/llmQuota.js, response code
 * 'API_KEY_REQUIRED') fires a window CustomEvent — see the response
 * interceptor in services/api.js — which this modal listens for, so it
 * pops up regardless of which page/button triggered the request.
 *
 * "Upgrade to Pro" is foregrounded over "Add your own API key" — not every
 * user is technical enough to want to manage a provider key themselves.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyRound, Crown, X } from 'lucide-react';
import UpgradeModal from './UpgradeModal.tsx';

export default function ApiKeyGateModal() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener('lyra:api-key-required', handler);
    return () => window.removeEventListener('lyra:api-key-required', handler);
  }, []);

  if (showUpgrade) {
    return <UpgradeModal isOpen onClose={() => { setShowUpgrade(false); setOpen(false); }} />;
  }
  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 10000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
        background: 'rgba(10,18,14,0.65)', backdropFilter: 'blur(8px)',
        fontFamily: "'DM Sans', sans-serif",
      }}
      onClick={(e) => e.target === e.currentTarget && setOpen(false)}
    >
      <div style={{
        position: 'relative', width: '100%', maxWidth: 420,
        background: 'var(--lyra-color-surface)', borderRadius: 20, boxShadow: '0 24px 64px rgba(0,0,0,.18)',
        padding: '28px 26px', border: '1px solid var(--lyra-color-border)',
      }}>
        <button
          onClick={() => setOpen(false)}
          style={{
            position: 'absolute', top: 14, right: 14, background: 'var(--lyra-color-surface-2)', border: 'none', cursor: 'pointer',
            width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--lyra-color-text-muted)',
          }}
        >
          <X size={13} />
        </button>

        <div style={{
          width: 44, height: 44, borderRadius: 12, background: 'var(--lyra-color-warning-bg)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14,
        }}>
          <KeyRound size={20} color="var(--lyra-color-warning)" />
        </div>

        <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--lyra-color-text)', marginBottom: 6 }}>
          You've used your free messages
        </div>
        <div style={{ fontSize: 13.5, color: 'var(--lyra-color-text-muted)', lineHeight: 1.55, marginBottom: 22 }}>
          Upgrade to Pro and we'll take care of everything, or add your own API key to keep going for free.
        </div>

        <button
          onClick={() => setShowUpgrade(true)}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            border: 'none', background: 'var(--app-primary-mid)', color: 'var(--app-on-accent)', borderRadius: 11, padding: '12px 0',
            fontSize: 13.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
            boxShadow: '0 4px 14px rgba(58,107,92,.35)', marginBottom: 10,
          }}
        >
          <Crown size={14} color="var(--lyra-color-warning)" /> Upgrade to Pro
        </button>
        <button
          onClick={() => { setOpen(false); navigate('/ai-console'); }}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            border: '1.5px solid var(--lyra-color-border)', background: 'var(--lyra-color-surface)', color: 'var(--lyra-color-text)', borderRadius: 11, padding: '11px 0',
            fontSize: 13.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          <KeyRound size={14} /> Add my own API key
        </button>
      </div>
    </div>
  );
}
